import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, ExternalLink, Clock, Share, LoaderCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";
import apiService from "../../services/api/apiServices";

const BlogDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [blogDetail, setBlogDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Get initial data from navigation state or fetch from API
  const { blog: initialBlog, imgBasePath: initialImgPath } = location.state || {};
  const imgBasePath = initialImgPath || "https://aaoghumen.com/dev3/assets/blog_img/";

  const fetchBlogDetail = React.useCallback(async () => {
    if (!slug) return;
    
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getBlogDetail(slug);
      if (response.status && response.data && response.data.blogDetail && response.data.blogDetail.length > 0) {
        setBlogDetail(response.data.blogDetail[0]);
      } else {
        throw new Error("Blog detail not found");
      }
    } catch (err) {
      setError("Failed to load blog details. Please try again.");
      console.error("Blog detail fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchBlogDetail();
  }, [fetchBlogDetail]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleReadMore = () => {
    const currentBlog = blogDetail || initialBlog;
    if (currentBlog?.link) {
      window.open(currentBlog.link, '_blank');
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: blogDetail?.title || initialBlog?.title,
        text: blogDetail?.shortDesc || initialBlog?.shortDesc,
        url: window.location.href,
      });
    } else {
      // Fallback to copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const currentBlog = blogDetail || initialBlog;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
        <div className="h-full w-full flex justify-center items-center py-20">
          <div className="flex flex-col items-center justify-center">
            <LoaderCircle
              className="animate-spin"
              size={50}
              color={"#EA5722"}
              strokeWidth={2}
            />
            <span className="mt-4 text-gray-600 text-lg">Loading story...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !currentBlog) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
        <div className="p-4">
          <div className="max-w-2xl mx-auto pt-20">
            <div className="bg-white border border-red-200 rounded-2xl p-8 text-center shadow-lg">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-500 text-2xl">⚠️</span>
              </div>
              <p className="text-red-600 mb-6 text-lg">{error || "Story not found"}</p>
              <div className="space-x-4">
                <button
                  onClick={() => navigate("/blog")}
                  className="bg-gray-500 text-white px-6 py-3 rounded-xl hover:bg-gray-600 transition-colors"
                >
                  Back to Stories
                </button>
                {error && (
                  <button
                    onClick={fetchBlogDetail}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all"
                  >
                    Try Again
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const seoData = blogDetail?.seo?.[0] || {};

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoData.metaTitle || currentBlog.title}</title>
        <meta name="description" content={seoData.metaDescription || currentBlog.shortDesc} />
        <meta name="keywords" content={seoData.keywords?.join(', ') || 'travel, blog, destination'} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={seoData.ogTitle || currentBlog.title} />
        <meta property="og:description" content={seoData.ogDescription || currentBlog.shortDesc} />
        <meta property="og:image" content={`${imgBasePath}${(seoData.ogImage || currentBlog.img).replace(/\.(jpeg|jpg|png)$/i, '')}.jpg`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="article" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content={seoData.twitterCard || "summary_large_image"} />
        <meta name="twitter:title" content={seoData.ogTitle || currentBlog.title} />
        <meta name="twitter:description" content={seoData.ogDescription || currentBlog.shortDesc} />
        <meta name="twitter:image" content={`${imgBasePath}${(seoData.ogImage || currentBlog.img).replace(/\.(jpeg|jpg|png)$/i, '')}.jpg`} />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Aaoghumen" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
        {/* Header with Navigation */}
        <div className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate("/blog")}
                className="flex items-center text-gray-600 hover:text-orange-600 transition-colors group"
              >
                <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium">Back to Stories</span>
              </button>
              <button
                onClick={handleShare}
                className="flex items-center text-gray-600 hover:text-orange-600 transition-colors group"
              >
                <Share className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                <span className="font-medium">Share</span>
              </button>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="relative">
          <div className="h-96 sm:h-[500px] lg:h-[600px] relative overflow-hidden">
            <img
              src={`${imgBasePath}${currentBlog.img.replace(/\.(jpeg|jpg|png)$/i, '')}.jpg`}
              alt={currentBlog.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/1200x600/f97316/ffffff?text=Image+Not+Found';
                e.target.onerror = null;
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
          </div>
          
          {/* Floating Content Card */}
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 -mt-32 relative z-10 border border-gray-100">
              <div className="flex flex-wrap items-center gap-4 mb-6 text-gray-600">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-orange-500" />
                  <span className="font-medium">{formatDate(currentBlog.date)}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-orange-500" />
                  <span className="font-medium">8 min read</span>
                </div>
                <div className="ml-auto">
                  <span className="bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold">
                    Travel Guide
                  </span>
                </div>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-6 leading-tight">
                {currentBlog.title}
              </h1>

              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                {currentBlog.shortDesc}
              </p>

              {/* Article Content */}
              {blogDetail?.lognDesc && (
                <div className="prose prose-lg prose-orange max-w-none mb-12">
                  <div 
                    className="text-gray-700 leading-relaxed text-lg"
                    dangerouslySetInnerHTML={{ __html: blogDetail.lognDesc }}
                  />
                </div>
              )}

              {/* Call to Action */}
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-4">Ready to Explore {currentBlog.title.split(',')[0]}?</h3>
                <p className="text-orange-100 mb-6 text-lg">
                  Discover more amazing destinations and start planning your perfect trip
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  {currentBlog.link && (
                    <button
                      onClick={handleReadMore}
                      className="inline-flex items-center justify-center bg-white text-orange-600 px-6 py-3 rounded-xl hover:bg-gray-100 transition-colors font-semibold shadow-lg"
                    >
                      Read Full Article
                      <ExternalLink className="w-5 h-5 ml-2" />
                    </button>
                  )}
                  <button
                    onClick={() => navigate("/")}
                    className="inline-flex items-center justify-center bg-orange-400 text-white px-6 py-3 rounded-xl hover:bg-orange-500 transition-colors font-semibold"
                  >
                    Plan Your Trip
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Discover More Amazing Stories
            </h2>
            <p className="text-gray-600 mb-6 text-lg">
              Explore our collection of travel guides and hidden gems
            </p>
            <button
              onClick={() => navigate("/blog")}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              View All Stories
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogDetail;